import React from "react";
import { Box, Button, Typography } from "@mui/material";
import { ErrorOutline as ErrorIcon, Refresh as RefreshIcon, Home as HomeIcon } from "@mui/icons-material";
import * as Sentry from "@sentry/react";
import { RTL } from "../../cache/CacheRtl";
import { colors, borders } from "../ui/colors";

/**
 * Catches render errors anywhere below it and shows a recoverable screen instead of React's
 * default — which is to unmount the entire tree, leaving a blank white page with nothing but a
 * console message the trainer will never see.
 *
 * This is a class because `componentDidCatch` / `getDerivedStateFromError` have no hooks
 * equivalent; there is no way to write an error boundary as a function component.
 *
 * Note what it does NOT catch (React's limits, not ours): errors thrown in event handlers, in
 * async callbacks / promise rejections, or during SSR. Sentry's global handlers pick those up —
 * see initSentry — but they won't render this screen.
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, eventId: null };
  }

  static getDerivedStateFromError() {
    // Runs first and must be pure — flip to the fallback UI here, report in componentDidCatch.
    return { hasError: true };
  }

  componentDidCatch(error, errorInfo) {
    // Keep the React component stack: it's the difference between "TypeError: undefined" and
    // knowing which component threw. Sentry returns an id we surface so a user can quote it.
    const eventId = Sentry.captureException(error, {
      contexts: { react: { componentStack: errorInfo?.componentStack } },
    });
    this.setState({ eventId });
  }

  handleReload = () => window.location.reload();

  // A hard navigation, not react-router: the router lives INSIDE the tree that just crashed, so
  // a soft navigate would re-render the same broken state. Reloading the document is the only
  // reliable way back.
  handleGoHome = () => { window.location.href = "/app/dashboard"; };

  render() {
    if (!this.state.hasError) return this.props.children;

    return (
      <RTL>
        <Box
          dir="rtl"
          data-testid="error-boundary-fallback"
          sx={{
            minHeight: "100vh",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 2,
            padding: 3,
            backgroundColor: colors.bg,
            textAlign: "center",
          }}
        >
          <Box sx={{
            width: 64, height: 64, borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            backgroundColor: colors.errorBg,
          }}>
            <ErrorIcon sx={{ fontSize: 34, color: colors.error }} />
          </Box>

          <Typography sx={{ fontSize: "22px", fontWeight: 700, color: colors.textPrimary }}>
            משהו השתבש
          </Typography>
          <Typography sx={{ fontSize: "14px", color: colors.textSecondary, maxWidth: 420 }}>
            אירעה שגיאה בלתי צפויה. ניתן לרענן את הדף או לחזור לדשבורד.
          </Typography>

          <Box sx={{ display: "flex", gap: 1.5, mt: 1, flexWrap: "wrap", justifyContent: "center" }}>
            <Button
              variant="contained"
              startIcon={<RefreshIcon />}
              onClick={this.handleReload}
              sx={{
                backgroundColor: colors.primary,
                textTransform: "none",
                borderRadius: borders.radius.md,
                padding: "8px 22px",
                fontWeight: 600,
                boxShadow: "none",
                "&:hover": { backgroundColor: colors.primaryDark, boxShadow: "none" },
              }}
            >
              רענן דף
            </Button>
            <Button
              variant="outlined"
              startIcon={<HomeIcon />}
              onClick={this.handleGoHome}
              sx={{
                borderColor: colors.border,
                color: colors.textSecondary,
                textTransform: "none",
                borderRadius: borders.radius.md,
                padding: "8px 22px",
                fontWeight: 600,
                "&:hover": { borderColor: colors.borderHover, backgroundColor: colors.bgHover },
              }}
            >
              חזרה לדשבורד
            </Button>
          </Box>

          {/* The support handle: without it a bug report is "it broke", with it we can find the
              exact event. Only rendered when Sentry actually returned an id. */}
          {this.state.eventId && (
            <Typography sx={{ fontSize: "12px", color: colors.textMuted, mt: 1 }}>
              קוד שגיאה: {this.state.eventId}
            </Typography>
          )}
        </Box>
      </RTL>
    );
  }
}

export default ErrorBoundary;
